/**
 * CandleScript - Indicator Templates
 * Pre-built indicator templates users can start from.
 */

export const TEMPLATES = {
    'moving-average': {
        id: 'moving-average',
        name: 'Moving Average',
        description: 'Simple or Exponential Moving Average overlay',
        category: 'trend',
        side: 'onchart',
        code: `function indicator(input, options) {
    const { close } = input;
    const len = options.length || 20;
    const type = options.type || 'sma';

    const avg = type === 'ema' ? ema(close, len) : sma(close, len);

    const result = [];
    for (let i = 0; i < close.length; i++) {
        result.push({ value: avg[i] });
    }
    return result;
}`,
        options: [
            { key: 'length', label: 'Length', type: 'number', default: 20, min: 1, max: 500 },
            { key: 'type', label: 'Type', type: 'select', default: 'sma', choices: ['sma', 'ema'] },
        ],
        plotConfig: {
            outputs: [{ key: 'value', label: 'MA', color: '#2962ff', width: 2, style: 'line' }],
        },
    },

    'rsi': {
        id: 'rsi',
        name: 'RSI',
        description: 'Relative Strength Index with overbought/oversold levels',
        category: 'oscillator',
        side: 'offchart',
        code: `function indicator(input, options) {
    const { close } = input;
    const len = options.length || 14;
    const overbought = options.overbought || 70;
    const oversold = options.oversold || 30;

    const r = rsi(close, len);

    const result = [];
    for (let i = 0; i < close.length; i++) {
        let signal = null;
        if (r[i] >= overbought) signal = 'sell';
        else if (r[i] <= oversold) signal = 'buy';

        result.push({
            value: r[i],
            signal: signal,
        });
    }
    return result;
}`,
        options: [
            { key: 'length', label: 'Length', type: 'number', default: 14, min: 1, max: 200 },
            { key: 'overbought', label: 'Overbought', type: 'number', default: 70, min: 50, max: 100 },
            { key: 'oversold', label: 'Oversold', type: 'number', default: 30, min: 0, max: 50 },
        ],
        plotConfig: {
            outputs: [{ key: 'value', label: 'RSI', color: '#7e57c2', width: 1.5, style: 'line' }],
            bands: [
                { value: 70, color: '#ef5350', style: 'dashed', label: 'Overbought' },
                { value: 30, color: '#26a69a', style: 'dashed', label: 'Oversold' },
            ],
            range: [0, 100],
        },
    },

    'macd': {
        id: 'macd',
        name: 'MACD',
        description: 'Moving Average Convergence Divergence',
        category: 'oscillator',
        side: 'offchart',
        code: `function indicator(input, options) {
    const { close } = input;
    const fastLen = options.fastLength || 12;
    const slowLen = options.slowLength || 26;
    const sigLen = options.signalLength || 9;

    const m = macd(close, fastLen, slowLen, sigLen);

    return {
        macdLine: Array.from(m.macd),
        signalLine: Array.from(m.signal),
        histogram: Array.from(m.histogram),
    };
}`,
        options: [
            { key: 'fastLength', label: 'Fast Length', type: 'number', default: 12, min: 1, max: 100 },
            { key: 'slowLength', label: 'Slow Length', type: 'number', default: 26, min: 1, max: 200 },
            { key: 'signalLength', label: 'Signal Length', type: 'number', default: 9, min: 1, max: 50 },
        ],
        plotConfig: {
            outputs: [
                { key: 'macdLine', label: 'MACD', color: '#2962ff', width: 1.5, style: 'line' },
                { key: 'signalLine', label: 'Signal', color: '#ff6d00', width: 1.5, style: 'line' },
                { key: 'histogram', label: 'Histogram', color: '#26a69a', width: 0, style: 'histogram' },
            ],
        },
    },

    'supertrend': {
        id: 'supertrend',
        name: 'SuperTrend',
        description: 'SuperTrend indicator with trend direction signals',
        category: 'trend',
        side: 'onchart',
        code: `function indicator(input, options) {
    const { high, low, close } = input;
    const len = options.length || 10;
    const mult = options.multiplier || 3.0;
    const n = close.length;

    const atrVal = atr(high, low, close, len);
    const result = [];

    let upperBand = 0, lowerBand = 0;
    let prevUpperBand = 0, prevLowerBand = 0;
    let supertrend = 0, prevSupertrend = 0;
    let direction = 1;

    for (let i = 0; i < n; i++) {
        if (isNaN(atrVal[i])) {
            result.push({ value: NaN, signal: null });
            continue;
        }

        const mid = (high[i] + low[i]) / 2;
        upperBand = mid + mult * atrVal[i];
        lowerBand = mid - mult * atrVal[i];

        if (i > 0) {
            if (lowerBand > prevLowerBand || close[i - 1] < prevLowerBand) {
                // keep lowerBand
            } else {
                lowerBand = prevLowerBand;
            }

            if (upperBand < prevUpperBand || close[i - 1] > prevUpperBand) {
                // keep upperBand
            } else {
                upperBand = prevUpperBand;
            }
        }

        if (i === 0) {
            direction = 1;
            supertrend = lowerBand;
        } else if (prevSupertrend === prevUpperBand) {
            direction = close[i] > upperBand ? 1 : -1;
        } else {
            direction = close[i] < lowerBand ? -1 : 1;
        }

        supertrend = direction === 1 ? lowerBand : upperBand;

        result.push({
            value: supertrend,
            signal: direction === 1 ? 'buy' : 'sell',
        });

        prevUpperBand = upperBand;
        prevLowerBand = lowerBand;
        prevSupertrend = supertrend;
    }

    return result;
}`,
        options: [
            { key: 'length', label: 'ATR Length', type: 'number', default: 10, min: 1, max: 100 },
            { key: 'multiplier', label: 'Multiplier', type: 'number', default: 3.0, min: 0.1, max: 10, step: 0.1 },
        ],
        plotConfig: {
            outputs: [{ key: 'value', label: 'SuperTrend', color: '#26a69a', width: 2, style: 'line' }],
            colorBySignal: true,
            signalColors: { buy: '#26a69a', sell: '#ef5350' },
        },
    },

    'bollinger-bands': {
        id: 'bollinger-bands',
        name: 'Bollinger Bands',
        description: 'Bollinger Bands with upper, middle, and lower bands',
        category: 'volatility',
        side: 'onchart',
        code: `function indicator(input, options) {
    const { close } = input;
    const len = options.length || 20;
    const mult = options.multiplier || 2.0;

    const bb = bbands(close, len, mult);

    return {
        upper: Array.from(bb.upper),
        middle: Array.from(bb.middle),
        lower: Array.from(bb.lower),
    };
}`,
        options: [
            { key: 'length', label: 'Length', type: 'number', default: 20, min: 1, max: 200 },
            { key: 'multiplier', label: 'StdDev Multiplier', type: 'number', default: 2.0, min: 0.1, max: 5, step: 0.1 },
        ],
        plotConfig: {
            outputs: [
                { key: 'upper', label: 'Upper', color: '#2962ff', width: 1, style: 'line' },
                { key: 'middle', label: 'Middle', color: '#ff9800', width: 1, style: 'dashed' },
                { key: 'lower', label: 'Lower', color: '#2962ff', width: 1, style: 'line' },
            ],
            fill: { from: 'upper', to: 'lower', color: 'rgba(41, 98, 255, 0.06)' },
        },
    },

    'blank': {
        id: 'blank',
        name: 'Blank Template',
        description: 'Start from scratch with a minimal template',
        category: 'custom',
        side: 'offchart',
        code: `function indicator(input, options) {
    const { open, high, low, close, volume } = input;
    const len = options.length || 14;

    // Your indicator logic here
    // Available functions: sma, ema, rsi, atr, highest, lowest,
    //   sum, stddev, crossover, crossunder, bbands, macd
    //
    // Drawing API: plot.line(), plot.hline(), plot.shape(),
    //   plot.arrow(), plot.text(), plot.candle(), plot.fill(),
    //   plot.bgcolor(), plot.histogram()

    const avg = sma(close, len);

    const result = [];
    for (let i = 0; i < close.length; i++) {
        result.push({
            value: avg[i],
        });
    }
    return result;
}`,
        options: [
            { key: 'length', label: 'Length', type: 'number', default: 14, min: 1, max: 500 },
        ],
        plotConfig: {
            outputs: [{ key: 'value', label: 'Output', color: '#2962ff', width: 1.5, style: 'line' }],
        },
    },

    'ema-crossover-signals': {
        id: 'ema-crossover-signals',
        name: 'EMA Crossover Signals',
        description: 'Dual EMA crossover with buy/sell arrows and colored background',
        category: 'trend',
        side: 'onchart',
        code: `function indicator(input, options) {
    const { high, low, close } = input;
    const fastLen = options.fastLength || 9;
    const slowLen = options.slowLength || 21;

    const fastEma = ema(close, fastLen);
    const slowEma = ema(close, slowLen);
    const cross_up = crossover(fastEma, slowEma);
    const cross_dn = crossunder(fastEma, slowEma);

    // Draw the two EMA lines
    plot.line(Array.from(fastEma), { color: '#26a69a', width: 2, title: 'Fast EMA' });
    plot.line(Array.from(slowEma), { color: '#ef5350', width: 2, title: 'Slow EMA' });

    // Draw buy/sell arrows at crossover points
    const buyArr = [];
    const sellArr = [];
    const bgColors = [];

    let inUptrend = false;
    for (let i = 0; i < close.length; i++) {
        if (cross_up[i]) {
            buyArr.push(low[i] * 0.998);
            inUptrend = true;
        } else {
            buyArr.push(NaN);
        }
        if (cross_dn[i]) {
            sellArr.push(high[i] * 1.002);
            inUptrend = false;
        } else {
            sellArr.push(NaN);
        }
        bgColors.push(inUptrend ? 'rgba(38,166,154,0.04)' : 'rgba(239,83,80,0.04)');
    }

    plot.arrow(buyArr, { direction: 'up', color: '#26a69a' });
    plot.arrow(sellArr, { direction: 'down', color: '#ef5350' });
    plot.bgcolor(bgColors);

    return Array.from(fastEma);
}`,
        options: [
            { key: 'fastLength', label: 'Fast Length', type: 'number', default: 9, min: 1, max: 100 },
            { key: 'slowLength', label: 'Slow Length', type: 'number', default: 21, min: 1, max: 200 },
        ],
        plotConfig: {
            outputs: [{ key: 'value', label: 'Fast EMA', color: '#26a69a', width: 2, style: 'line' }],
        },
    },

    'heikin-ashi': {
        id: 'heikin-ashi',
        name: 'Heikin-Ashi Candles',
        description: 'Heikin-Ashi smoothed candles overlay',
        category: 'trend',
        side: 'onchart',
        code: `function indicator(input, options) {
    const { open, high, low, close } = input;
    const n = close.length;

    const haClose = new Float64Array(n);
    const haOpen = new Float64Array(n);
    const haHigh = new Float64Array(n);
    const haLow = new Float64Array(n);

    // First bar
    haClose[0] = (open[0] + high[0] + low[0] + close[0]) / 4;
    haOpen[0] = (open[0] + close[0]) / 2;
    haHigh[0] = high[0];
    haLow[0] = low[0];

    for (let i = 1; i < n; i++) {
        haClose[i] = (open[i] + high[i] + low[i] + close[i]) / 4;
        haOpen[i] = (haOpen[i - 1] + haClose[i - 1]) / 2;
        haHigh[i] = Math.max(high[i], haOpen[i], haClose[i]);
        haLow[i] = Math.min(low[i], haOpen[i], haClose[i]);
    }

    // Draw Heikin-Ashi candles
    plot.candle(
        { open: haOpen, high: haHigh, low: haLow, close: haClose },
        { colorUp: '#26a69a', colorDown: '#ef5350', title: 'Heikin-Ashi' }
    );

    return Array.from(haClose);
}`,
        options: [],
        plotConfig: {
            outputs: [{ key: 'value', label: 'HA Close', color: '#ff9800', width: 0, style: 'line' }],
        },
    },

    'support-resistance-table': {
        id: 'support-resistance-table',
        name: 'Support Resistance Levels',
        description: 'Display support and resistance levels with labels',
        category: 'custom',
        side: 'onchart',
        code: `function indicator(input, options) {
    const { high, low, close } = input;
    const lookback = options.lookback || 20;
    const multiplier = options.multiplier || 0.1;
    const n = close.length;

    // Calculate support and resistance based on recent price action
    const support = [];
    const resistance = [];

    for (let i = 0; i < n; i++) {
        const start = Math.max(0, i - lookback);
        const recentHigh = highest(high, start, i);
        const recentLow = lowest(low, start, i);
        const range = recentHigh - recentLow;

        support.push(recentLow - range * multiplier);
        resistance.push(recentHigh + range * multiplier);
    }

    // Draw horizontal lines for current support/resistance
    const currentSupport = support[n - 1];
    const currentResistance = resistance[n - 1];

    plot.hline(currentSupport, { color: '#26a69a', width: 1, style: 'dashed', title: 'Support' });
    plot.hline(currentResistance, { color: '#ef5350', width: 1, style: 'dashed', title: 'Resistance' });

    // Add text labels at the last bar showing current levels
    const lastBar = n - 1;
    const supportStr = Number(currentSupport).toFixed(2);
    const resistanceStr = Number(currentResistance).toFixed(2);
    plot.text(lastBar, currentSupport, 'S: ' + supportStr, { color: '#26a69a', size: 11, location: 'absolute' });
    plot.text(lastBar, currentResistance, 'R: ' + resistanceStr, { color: '#ef5350', size: 11, location: 'absolute' });

    return {
        support: support,
        resistance: resistance,
    };
}`,
        options: [
            { key: 'lookback', label: 'Lookback Period', type: 'number', default: 20, min: 5, max: 100 },
            { key: 'multiplier', label: 'Range Multiplier', type: 'number', default: 0.1, min: 0.01, max: 0.5, step: 0.01 },
        ],
        plotConfig: {
            outputs: [
                { key: 'support', label: 'Support', color: '#26a69a', width: 1, style: 'line' },
                { key: 'resistance', label: 'Resistance', color: '#ef5350', width: 1, style: 'line' },
            ],
        },
    },
};

export const TEMPLATE_LIST = Object.values(TEMPLATES);

export function getTemplate(id) {
    return TEMPLATES[id] || null;
}

export default TEMPLATES;
